import { create } from "zustand";
import { findAllCidades, findAllEstados, findAllRegionais } from "API/apiCidadesRegionais";
import { Cidade, Estado, RegionalPoloCity } from "types/Cidade";

interface MunicipioFilter {
  nome?: string;
  sigla?: string;
  estado_id?: number | null;
}

interface LocalidadeState {
  estados: Estado[];
  regionais: RegionalPoloCity[];
  cidadesCache: Record<string, Cidade[]>;
  ensureEstados: (force?: boolean) => Promise<Estado[]>;
  ensureRegionais: (force?: boolean) => Promise<RegionalPoloCity[]>;
  ensureCidades: (filter?: MunicipioFilter, force?: boolean) => Promise<Cidade[]>;
  clearAll: () => void;
}

const normalizeFilter = (filter?: MunicipioFilter) => ({
  nome: filter?.nome?.trim() ?? "",
  sigla: filter?.sigla?.trim() ?? "",
  estado_id: filter?.estado_id ?? null,
});

const makeFilterKey = (filter?: MunicipioFilter) => {
  const normalized = normalizeFilter(filter);
  return JSON.stringify(normalized);
};

let estadosPromise: Promise<Estado[]> | null = null;
let regionaisPromise: Promise<RegionalPoloCity[]> | null = null;
const cidadesPromises = new Map<string, Promise<Cidade[]>>();

export const useLocalidadeStore = create<LocalidadeState>((set, get) => ({
  estados: [],
  regionais: [],
  cidadesCache: {},

  ensureEstados: async (force = false) => {
    if (!force && get().estados.length > 0) {
      return get().estados;
    }

    if (estadosPromise) {
      return estadosPromise;
    }

    estadosPromise = (async () => {
      const data = await findAllEstados();
      set({ estados: data });
      return data;
    })().finally(() => {
      estadosPromise = null;
    });

    return estadosPromise;
  },

  ensureRegionais: async (force = false) => {
    if (!force && get().regionais.length > 0) {
      return get().regionais;
    }

    if (regionaisPromise) {
      return regionaisPromise;
    }

    regionaisPromise = (async () => {
      const data = await findAllRegionais();
      set({ regionais: data });
      return data;
    })().finally(() => {
      regionaisPromise = null;
    });

    return regionaisPromise;
  },

  ensureCidades: async (filter, force = false) => {
    const normalized = normalizeFilter(filter);
    const hasValidFilter = Boolean(normalized.nome || normalized.sigla || normalized.estado_id);
    if (!hasValidFilter) {
      return [];
    }

    const key = makeFilterKey(normalized);
    const cached = get().cidadesCache[key];

    if (!force && cached) {
      return cached;
    }

    const promiseInFlight = cidadesPromises.get(key);
    if (promiseInFlight) {
      return promiseInFlight;
    }

    const promise = (async () => {
      let page = 1;
      let lastPage = 1;
      const cidades: Cidade[] = [];

      do {
        const response = await findAllCidades(
          page,
          200,
          normalized.estado_id,
          normalized.sigla || undefined,
          normalized.nome || undefined
        );

        cidades.push(...response.cidades);
        lastPage = response.pagination.last_page ?? response.pagination.total_pages ?? 1;
        page += 1;
      } while (page <= lastPage);

      set((state) => ({
        cidadesCache: {
          ...state.cidadesCache,
          [key]: cidades,
        },
      }));

      return cidades;
    })().finally(() => {
      cidadesPromises.delete(key);
    });

    cidadesPromises.set(key, promise);
    return promise;
  },

  clearAll: () =>
    set({
      estados: [],
      regionais: [],
      cidadesCache: {},
    }),
}));
